import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Clapperboard, Headphones, Music2, Play, RefreshCw, Sparkles, Volume2, Youtube } from "lucide-react";
import { SMALL_INLINE_ICON_SIZE } from "@/constants/ui";
import { api, formatApiErrorDetail } from "@/services/api";

const essentialWords = [
  { en: "Hello", pt: "Olá", hint: "comece qualquer conversa" },
  { en: "Please", pt: "Por favor", hint: "deixa o pedido educado" },
  { en: "Sorry", pt: "Desculpa / com licença", hint: "serve para interromper ou pedir perdão" },
  { en: "Help", pt: "Ajuda", hint: "palavra de emergência" },
  { en: "Water", pt: "Água", hint: "restaurante, mercado, médico" },
  { en: "Work", pt: "Trabalho", hint: "ambiente profissional" },
  { en: "Home", pt: "Casa", hint: "endereço e moradia" },
  { en: "Today", pt: "Hoje", hint: "tempo básico" },
];

const survivalPhrases = [
  { en: "I need help.", pt: "Eu preciso de ajuda.", blank: "I need ____.", answer: "help" },
  { en: "I don't understand.", pt: "Eu não entendo.", blank: "I don't ____.", answer: "understand" },
  { en: "Can you repeat, please?", pt: "Você pode repetir, por favor?", blank: "Can you ____, please?", answer: "repeat" },
  { en: "I'm learning English.", pt: "Estou aprendendo inglês.", blank: "I'm ____ English.", answer: "learning" },
  { en: "One moment, please.", pt: "Um momento, por favor.", blank: "One ____, please.", answer: "moment" },
];

const miniScenes = [
  {
    title: "Cena casual: corredor do prédio",
    vibe: "série do cotidiano",
    lines: ["Hi, are you new here?", "Yes, I just moved in.", "Nice to meet you.", "Nice to meet you too."],
  },
  {
    title: "Cena de filme: pedido rápido",
    vibe: "diálogo curto e realista",
    lines: ["Hey, let's go.", "Wait, I need water.", "Okay, one minute.", "Thanks, I'm ready."],
  },
  {
    title: "Cena profissional: primeiro turno",
    vibe: "trabalho simples",
    lines: ["Good morning.", "Good morning. I'm ready.", "Can you help at the front?", "Sure, I can help."],
  },
];

const rhythmDrills = [
  { focus: "wanna", safeLine: "I wanna learn today.", pt: "Eu quero aprender hoje." },
  { focus: "gonna", safeLine: "I'm gonna try again.", pt: "Eu vou tentar de novo." },
  { focus: "don't", safeLine: "I don't know yet.", pt: "Eu ainda não sei." },
  { focus: "let's", safeLine: "Let's start slow.", pt: "Vamos começar devagar." },
];

const DEFAULT_YOUTUBE_URL = "https://youtu.be/henIVlCPVIY?si=MF_uDZsYW2dqXKI3";
const MUSIC_YOUTUBE_URL = "https://youtu.be/RxWEvV9zYj4?si=92D4cUvqabWMTUy1";

const musicPracticeLines = [
  { en: "I'm here with you.", pt: "Estou aqui com você.", focus: "I'm = I am" },
  { en: "Don't stop now.", pt: "Não pare agora.", focus: "don't = do not" },
  { en: "We can try again.", pt: "Podemos tentar de novo.", focus: "can = poder/conseguir" },
  { en: "Let's keep going.", pt: "Vamos continuar.", focus: "let's = let us" },
];

function speak(text, rate = 0.82) {
  if (!window.speechSynthesis || !text) return;
  window.speechSynthesis.cancel();
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = "en-US";
  utterance.rate = rate;
  window.speechSynthesis.speak(utterance);
}

function getYoutubeEmbedUrl(rawUrl) {
  try {
    const url = new URL(rawUrl);
    const isShort = url.hostname.includes("youtu.be");
    const videoId = isShort ? url.pathname.replace("/", "").split("/")[0] : url.searchParams.get("v");
    if (!videoId) return "";
    const start = url.searchParams.get("t") || url.searchParams.get("start") || "";
    const seconds = start.toString().replace("s", "");
    const params = new URLSearchParams({ rel: "0", modestbranding: "1", playsinline: "1", cc_load_policy: "0" });
    if (seconds) params.set("start", seconds);
    return `https://www.youtube-nocookie.com/embed/${videoId}?${params.toString()}`;
  } catch (error) {
    return "";
  }
}

function VideoPlayer({ embedUrl, isLoaded, onClose, onLoad, title = "Vídeo integrado" }) {
  if (!embedUrl) {
    return <div className="youtube-placeholder mt-4" data-testid="zero-youtube-placeholder">Cole um link válido do YouTube para abrir o player.</div>;
  }

  if (!isLoaded) {
    return (
      <div className="youtube-placeholder mt-4" data-testid="zero-youtube-placeholder">
        <div className="grid gap-3 text-center">
          <strong data-testid="zero-youtube-lazy-title">{title}</strong>
          <span data-testid="zero-youtube-lazy-copy">Clique para carregar o vídeo dentro da aula. Isso evita travamentos ao usar legendas.</span>
          <button type="button" className="primary-action w-auto justify-self-center" onClick={onLoad} data-testid="zero-youtube-load-button">
            <Play size={18} /> Abrir vídeo integrado
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4 grid gap-3" data-testid="zero-youtube-loaded-player">
      <div className="youtube-frame-wrap" data-testid="zero-youtube-embed-wrap">
        <iframe
          src={embedUrl}
          title={title}
          loading="lazy"
          referrerPolicy="strict-origin-when-cross-origin"
          allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          data-testid="zero-youtube-iframe"
        />
      </div>
      <button type="button" className="secondary-action w-auto justify-self-start" onClick={onClose} data-testid="zero-youtube-unload-button">
        Fechar vídeo para liberar memória
      </button>
    </div>
  );
}

function WordsCard() {
  return (
    <article className="neo-card surface-mint p-5" data-testid="zero-words-card">
      <div className="zero-card-title">
        <Headphones size={24} />
        <div>
          <p className="section-kicker" data-testid="zero-words-kicker">passo 1</p>
          <h2 className="font-display text-3xl font-black" data-testid="zero-words-title">Palavras essenciais</h2>
        </div>
      </div>
      <div className="mt-5 grid gap-3 sm:grid-cols-2" data-testid="zero-word-list">
        {essentialWords.map((word) => (
          <button key={word.en} type="button" className="zero-word-button" onClick={() => speak(word.en, 0.75)} data-testid={`zero-word-button-${word.en.toLowerCase()}`}>
            <span data-testid={`zero-word-en-${word.en.toLowerCase()}`}>{word.en}</span>
            <small data-testid={`zero-word-pt-${word.en.toLowerCase()}`}>{word.pt}</small>
            <em data-testid={`zero-word-hint-${word.en.toLowerCase()}`}>{word.hint}</em>
          </button>
        ))}
      </div>
    </article>
  );
}

function PhrasesCard({ selectedPhrase, onSelectPhrase }) {
  return (
    <article className="neo-card surface-yellow p-5" data-testid="zero-phrases-card">
      <div className="zero-card-title">
        <Volume2 size={24} />
        <div>
          <p className="section-kicker" data-testid="zero-phrases-kicker">passo 2</p>
          <h2 className="font-display text-3xl font-black" data-testid="zero-phrases-title">Frases de sobrevivência</h2>
        </div>
      </div>
      <div className="mt-5 space-y-3" data-testid="zero-survival-list">
        {survivalPhrases.map((phrase) => (
          <button key={phrase.en} type="button" className={`zero-phrase-row ${selectedPhrase.en === phrase.en ? "active" : ""}`} onClick={() => onSelectPhrase(phrase)} data-testid={`zero-phrase-button-${phrase.answer}`}>
            <span data-testid={`zero-phrase-en-${phrase.answer}`}>{phrase.en}</span>
            <small data-testid={`zero-phrase-pt-${phrase.answer}`}>{phrase.pt}</small>
          </button>
        ))}
      </div>
    </article>
  );
}

function PracticeCard({ blankAnswer, blankResult, blankResultClass, onBlankAnswerChange, onQuizChoice, quizChoice, quizCorrect, selectedPhrase }) {
  return (
    <article className="neo-card surface-blue p-5" data-testid="zero-practice-card">
      <div className="zero-card-title">
        <CheckCircle2 size={24} />
        <div>
          <p className="section-kicker" data-testid="zero-practice-kicker">passo 3</p>
          <h2 className="font-display text-3xl font-black" data-testid="zero-practice-title">Complete e traduza</h2>
        </div>
      </div>
      <div className="mt-5 rounded-[8px] border-2 border-slate-950 bg-white p-4" data-testid="zero-blank-exercise">
        <p className="font-display text-2xl font-black" data-testid="zero-blank-prompt">{selectedPhrase.blank}</p>
        <input className="zero-input mt-3" value={blankAnswer} onChange={(event) => onBlankAnswerChange(event.target.value)} placeholder="Digite a palavra que falta" data-testid="zero-blank-input" />
        <p className={`mt-2 text-sm font-black ${blankResultClass}`} data-testid="zero-blank-result">{blankResult}</p>
      </div>
      <div className="mt-4 rounded-[8px] border-2 border-slate-950 bg-white p-4" data-testid="zero-translation-quiz">
        <p className="font-black" data-testid="zero-quiz-prompt">Tradução de “{selectedPhrase.en}”</p>
        <div className="mt-3 grid gap-2">
          {[selectedPhrase.pt, "Eu moro aqui.", "Bom trabalho."].map((choice) => (
            <button key={choice} type="button" className="zero-choice-button" onClick={() => onQuizChoice(choice)} data-testid={`zero-quiz-choice-${choice.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              {choice}
            </button>
          ))}
        </div>
        {quizChoice && <p className="mt-2 text-sm font-black" data-testid="zero-quiz-result">{quizCorrect ? "Boa! Tradução correta." : "Quase. Veja a tradução e tente de novo."}</p>}
      </div>
    </article>
  );
}

function ScenesCard() {
  return (
    <article className="neo-card surface-lavender p-5" data-testid="zero-scenes-card">
      <div className="zero-card-title">
        <Clapperboard size={24} />
        <div>
          <p className="section-kicker" data-testid="zero-scenes-kicker">filmes e séries</p>
          <h2 className="font-display text-3xl font-black" data-testid="zero-scenes-title">Mini cenas originais</h2>
        </div>
      </div>
      <div className="mt-5 grid gap-4" data-testid="zero-scene-list">
        {miniScenes.map((scene) => (
          <div className="zero-scene" key={scene.title} data-testid={`zero-scene-${scene.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <strong data-testid={`zero-scene-title-${scene.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{scene.title}</strong>
              <span className="label-chip bg-white" data-testid={`zero-scene-vibe-${scene.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{scene.vibe}</span>
            </div>
            <div className="mt-3 grid gap-2">
              {scene.lines.map((line) => (
                <button key={line} type="button" className="zero-line-button" onClick={() => speak(line, 0.8)} data-testid={`zero-scene-line-${line.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <Play size={SMALL_INLINE_ICON_SIZE} /> {line}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </article>
  );
}

function MusicCard({ isLoaded, onClose, onLoad }) {
  const musicEmbedUrl = getYoutubeEmbedUrl(MUSIC_YOUTUBE_URL);

  return (
    <article className="neo-card surface-neutral p-5" data-testid="zero-music-card">
      <div className="zero-card-title">
        <Music2 size={24} />
        <div>
          <p className="section-kicker" data-testid="zero-music-kicker">música</p>
          <h2 className="font-display text-3xl font-black" data-testid="zero-music-title">Ritmo e contrações</h2>
        </div>
      </div>
      <p className="mt-4 text-sm font-bold text-slate-600" data-testid="zero-music-description">
        Vídeo musical como referência externa + frases originais para treinar som, ritmo e contrações sem copiar letra protegida.
      </p>
      <VideoPlayer embedUrl={musicEmbedUrl} isLoaded={isLoaded} onClose={onClose} onLoad={onLoad} title="Vídeo musical para treino de ritmo" />
      <div className="mt-5 grid gap-3" data-testid="zero-rhythm-list">
        {rhythmDrills.map((drill) => (
          <button key={drill.focus} type="button" className="rhythm-row" onClick={() => speak(drill.safeLine, 0.72)} data-testid={`zero-rhythm-button-${drill.focus}`}>
            <span data-testid={`zero-rhythm-focus-${drill.focus}`}>{drill.focus}</span>
            <strong data-testid={`zero-rhythm-line-${drill.focus}`}>{drill.safeLine}</strong>
            <small data-testid={`zero-rhythm-pt-${drill.focus}`}>{drill.pt}</small>
          </button>
        ))}
      </div>
      <div className="mt-5 rounded-[8px] border-2 border-slate-950 bg-white p-4" data-testid="zero-music-safe-lines">
        <p className="text-xs font-black uppercase text-slate-500" data-testid="zero-music-safe-lines-kicker">prática segura inspirada em música</p>
        <div className="mt-3 grid gap-2">
          {musicPracticeLines.map((line) => (
            <button key={line.en} type="button" className="zero-line-button" onClick={() => speak(line.en, 0.74)} data-testid={`zero-music-practice-line-${line.en.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <Play size={SMALL_INLINE_ICON_SIZE} />
              <span>
                <strong>{line.en}</strong>
                <small className="block text-slate-500">{line.pt} · {line.focus}</small>
              </span>
            </button>
          ))}
        </div>
      </div>
    </article>
  );
}

function ZeroExerciseInput({ exercise, lessonId, onAnswer, state }) {
  if (exercise.type === "choice") {
    return (
      <div className="grammar-options" data-testid={`zero-lesson-options-${exercise.id}`}>
        {exercise.options.map((option) => <button key={option} type="button" className={`grammar-option-button ${state.answers[exercise.id] === option ? "grammar-option-selected" : ""}`} onClick={() => onAnswer(lessonId, exercise.id, option)} data-testid={`zero-lesson-option-${exercise.id}-${option.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{option}</button>)}
      </div>
    );
  }
  return <input className="auth-input mt-3" value={state.answers[exercise.id] || ""} onChange={(event) => onAnswer(lessonId, exercise.id, event.target.value)} placeholder="Digite a frase" data-testid={`zero-lesson-input-${exercise.id}`} />;
}

function ZeroExerciseCard({ exercise, lessonId, onAnswer, resultById, state }) {
  const result = resultById[exercise.id];
  return (
    <article className="grammar-exercise mt-4" data-testid={`zero-lesson-exercise-${exercise.id}`}>
      <p className="zero-label" data-testid={`zero-lesson-exercise-prompt-${exercise.id}`}>{exercise.prompt_pt}</p>
      <h4 className="mt-2 font-display text-2xl font-black" data-testid={`zero-lesson-exercise-question-${exercise.id}`}>{exercise.question}</h4>
      <ZeroExerciseInput exercise={exercise} lessonId={lessonId} onAnswer={onAnswer} state={state} />
      {result && <p className="mt-3 text-sm font-black text-slate-600" data-testid={`zero-lesson-result-${exercise.id}`}>Resposta: {result.expected_answer}. {result.explanation_pt}</p>}
    </article>
  );
}

function ZeroLessonStatus({ progress }) {
  if (progress.status === "completed") return <span className="profile-badge" data-testid={`zero-lesson-completed-${progress.lesson_id}`}>concluída</span>;
  if (progress.locked) return <span className="profile-badge" data-testid={`zero-lesson-locked-${progress.lesson_id}`}>bloqueada</span>;
  if (progress.recommended) return <span className="profile-badge" data-testid={`zero-lesson-current-${progress.lesson_id}`}>aula atual</span>;
  return null;
}

function ZeroLessonControls({ allAnswered, lessonId, onReview, onSubmit, pending, progress }) {
  if (progress.locked || progress.status === "completed") return null;
  return (
    <div className="mt-5 flex flex-wrap gap-3" data-testid={`zero-lesson-controls-${lessonId}`}>
      <button type="button" className="primary-action w-auto" onClick={() => onSubmit(lessonId)} disabled={pending || !allAnswered} data-testid={`zero-lesson-submit-button-${lessonId}`}>{progress.review_required ? "Tentar após revisão" : "Corrigir exercícios"}</button>
      {progress.review_required && <button type="button" className="secondary-action w-auto" onClick={() => onReview(lessonId)} data-testid={`zero-lesson-review-button-${lessonId}`}>Revisei, liberar tentativa</button>}
    </div>
  );
}

function ZeroLessonCard({ item, lessonState, onAnswer, onReview, onSubmit, pending }) {
  const { lesson, progress } = item;
  const state = lessonState[lesson.id] || { answers: {}, attemptResult: null, error: "" };
  const resultById = Object.fromEntries((state.attemptResult?.results || []).map((result) => [result.exercise_id, result]));
  const allAnswered = lesson.exercises.every((exercise) => (state.answers[exercise.id] || "").trim());
  return (
    <article className={`neo-card surface-neutral p-5 ${progress.locked ? "opacity-60" : ""}`} data-testid={`zero-lesson-card-${lesson.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="section-kicker" data-testid={`zero-lesson-order-${lesson.id}`}>aula {lesson.order}/7</p>
          <h3 className="mt-3 font-display text-3xl font-black" data-testid={`zero-lesson-title-${lesson.id}`}>{lesson.title}</h3>
        </div>
        <ZeroLessonStatus progress={progress} />
      </div>
      <p className="mt-4 text-sm font-bold text-slate-600" data-testid={`zero-lesson-objective-${lesson.id}`}>{lesson.objective_pt}</p>
      {!progress.locked && progress.status !== "completed" && <div className="next-step-box" data-testid={`zero-next-step-${lesson.id}`}>Próximo passo: responda os exercícios desta aula e clique em “Corrigir exercícios”.</div>}
      <div className="grammar-explanation mt-4" data-testid={`zero-lesson-explanation-${lesson.id}`}><p>{lesson.explanation_pt}</p></div>
      {!progress.locked && lesson.exercises.map((exercise) => <ZeroExerciseCard key={exercise.id} exercise={exercise} lessonId={lesson.id} onAnswer={onAnswer} resultById={resultById} state={state} />)}
      {state.attemptResult && <div className="grammar-score-panel mt-4" data-testid={`zero-lesson-attempt-result-${lesson.id}`}>{state.attemptResult.passed ? "Aula concluída. Próxima liberada." : "Revise as respostas antes de tentar novamente."}</div>}
      {state.error && <div className="auth-error mt-4" role="alert" data-testid={`zero-lesson-error-${lesson.id}`}>{state.error}</div>}
      <ZeroLessonControls allAnswered={allAnswered} lessonId={lesson.id} onReview={onReview} onSubmit={onSubmit} pending={pending} progress={progress} />
    </article>
  );
}

function ZeroLessonTrack({ lessonState, lessons, onAnswer, onReview, onSubmit, pending }) {
  if (!lessons.length) return <section className="neo-card surface-neutral mt-8 p-5" data-testid="zero-lessons-loading">Carregando aulas do zero...</section>;
  return (
    <section className="mt-8" data-testid="zero-lesson-track-section">
      <div className="page-title-row" data-testid="zero-lesson-track-title">
        <p className="section-kicker" data-testid="zero-track-kicker">7 aulas interativas</p>
        <h2 className="font-display text-4xl font-black" data-testid="zero-track-heading">Do Zero agora avança por exercício corrigido.</h2>
        <p className="max-w-3xl text-lg font-bold text-slate-600" data-testid="zero-track-description">Comece pela aula atual. Os exercícios aparecem dentro do card da aula.</p>
      </div>
      <div className="mt-6 grid gap-5 lg:grid-cols-2" data-testid="zero-lesson-grid">
        {lessons.map((item) => <ZeroLessonCard key={item.lesson.id} item={item} lessonState={lessonState} onAnswer={onAnswer} onReview={onReview} onSubmit={onSubmit} pending={pending} />)}
      </div>
    </section>
  );
}

export default function ZeroStart() {
  const queryClient = useQueryClient();
  const [selectedPhrase, setSelectedPhrase] = useState(survivalPhrases[0]);
  const [blankAnswer, setBlankAnswer] = useState("");
  const [quizChoice, setQuizChoice] = useState("");
  const [youtubeUrl, setYoutubeUrl] = useState(DEFAULT_YOUTUBE_URL);
  const [clipPhrase, setClipPhrase] = useState("I need help.");
  const [customSnippet, setCustomSnippet] = useState("");
  const [customTranslation, setCustomTranslation] = useState("");
  const [loadedVideo, setLoadedVideo] = useState(null);
  const [zeroLessonState, setZeroLessonState] = useState({});
  const { data: zeroLessons = [] } = useQuery({ queryKey: ["zero-lessons"], queryFn: api.getZeroLessons });
  const zeroMutation = useMutation({
    mutationFn: ({ lessonId }) => api.submitZeroAttempt(lessonId, Object.entries(zeroLessonState[lessonId]?.answers || {}).map(([exercise_id, answer]) => ({ exercise_id, answer })), Boolean(zeroLessonState[lessonId]?.reviewConfirmed)),
    onSuccess: (result) => {
      setZeroLessonState((current) => ({ ...current, [result.lesson_id]: { ...(current[result.lesson_id] || {}), attemptResult: result, error: "", reviewConfirmed: false } }));
      queryClient.invalidateQueries({ queryKey: ["zero-lessons"] });
      queryClient.invalidateQueries({ queryKey: ["recommendations"] });
    },
    onError: (error, variables) => setZeroLessonState((current) => ({ ...current, [variables.lessonId]: { ...(current[variables.lessonId] || { answers: {} }), error: formatApiErrorDetail(error.response?.data?.detail) } })),
  });

  const embedUrl = getYoutubeEmbedUrl(youtubeUrl);
  const blankCorrect = blankAnswer.trim().toLowerCase() === selectedPhrase.answer;
  const quizCorrect = quizChoice === selectedPhrase.pt;
  let blankResult = "Escolha uma frase ao lado e complete a lacuna.";
  let blankResultClass = "text-slate-500";
  if (blankAnswer) {
    blankResult = blankCorrect ? "Certo! Agora fale a frase completa." : "Continue tentando. Ouça a frase e repita devagar.";
    blankResultClass = blankCorrect ? "text-green-700" : "text-slate-500";
  }
  const selectPhrase = (phrase) => {
    setSelectedPhrase(phrase);
    setBlankAnswer("");
    setQuizChoice("");
    speak(phrase.en, 0.78);
  };
  const handleZeroAnswer = (lessonId, exerciseId, answer) => setZeroLessonState((current) => ({ ...current, [lessonId]: { answers: { ...(current[lessonId]?.answers || {}), [exerciseId]: answer }, attemptResult: null, error: "", reviewConfirmed: current[lessonId]?.reviewConfirmed || false } }));
  const handleZeroReview = (lessonId) => setZeroLessonState((current) => ({ ...current, [lessonId]: { ...(current[lessonId] || { answers: {} }), reviewConfirmed: true, error: "Revisão confirmada. Agora tente novamente." } }));

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8" data-testid="zero-page">
      <section className="zero-hero" data-testid="zero-hero-section">
        <div className="max-w-4xl">
          <p className="section-kicker" data-testid="zero-kicker">inglês do zero</p>
          <h1 className="mt-4 font-display text-4xl font-black leading-none sm:text-5xl lg:text-6xl" data-testid="zero-heading">
            Um start simples para quem ainda não sabe nada.
          </h1>
          <p className="mt-5 max-w-3xl text-base font-bold text-slate-700 md:text-lg" data-testid="zero-description">
            Palavras essenciais, frases de sobrevivência, mini cenas originais no clima de filmes/séries e treino de ritmo inspirado em música — sem copiar letras ou roteiros protegidos.
          </p>
        </div>
      </section>

      <section className="zero-grid mt-8" data-testid="zero-learning-grid">
        <WordsCard />
        <PhrasesCard selectedPhrase={selectedPhrase} onSelectPhrase={selectPhrase} />
        <PracticeCard blankAnswer={blankAnswer} blankResult={blankResult} blankResultClass={blankResultClass} onBlankAnswerChange={setBlankAnswer} onQuizChoice={setQuizChoice} quizChoice={quizChoice} quizCorrect={quizCorrect} selectedPhrase={selectedPhrase} />
        <ScenesCard />
      </section>

      <ZeroLessonTrack lessonState={zeroLessonState} lessons={zeroLessons} onAnswer={handleZeroAnswer} onReview={handleZeroReview} onSubmit={(lessonId) => zeroMutation.mutate({ lessonId })} pending={zeroMutation.isPending} />

      <section className="mt-8 grid gap-6 lg:grid-cols-[0.95fr_1.05fr]" data-testid="zero-media-section">
        <MusicCard isLoaded={loadedVideo === "music"} onClose={() => setLoadedVideo(null)} onLoad={() => setLoadedVideo("music")} />
        <article className="neo-card surface-yellow p-5" data-testid="zero-youtube-card">
          <div className="zero-card-title">
            <Youtube size={26} />
            <div>
              <p className="section-kicker" data-testid="zero-youtube-kicker">youtube</p>
              <h2 className="font-display text-3xl font-black" data-testid="zero-youtube-title">Use um link como referência</h2>
            </div>
          </div>
          <p className="mt-4 text-sm font-bold text-slate-700" data-testid="zero-youtube-description">
            Vídeo exemplo já incluído para a aula. O app não copia áudio, letra ou roteiro; o YouTube fica como referência externa e você pratica frases seguras/originais.
          </p>
          <input className="zero-input mt-4" value={youtubeUrl} onChange={(event) => setYoutubeUrl(event.target.value)} placeholder="Cole o link do YouTube aqui" data-testid="zero-youtube-input" />
          <VideoPlayer embedUrl={embedUrl} isLoaded={loadedVideo === "main"} onClose={() => setLoadedVideo(null)} onLoad={() => setLoadedVideo("main")} title="Vídeo exemplo da aula Inglês do Zero" />
          <div className="mt-4 grid gap-3 md:grid-cols-2" data-testid="zero-clip-practice">
            <div>
              <label className="zero-label" htmlFor="clipPhrase" data-testid="zero-clip-phrase-label">Frase segura para praticar</label>
              <input id="clipPhrase" className="zero-input mt-2" value={clipPhrase} onChange={(event) => setClipPhrase(event.target.value)} data-testid="zero-clip-phrase-input" />
              <button type="button" className="secondary-action mt-3" onClick={() => speak(clipPhrase, 0.76)} data-testid="zero-clip-speak-button"><Volume2 size={18} /> Ouvir frase</button>
            </div>
            <div>
              <label className="zero-label" htmlFor="customSnippet" data-testid="zero-custom-snippet-label">Trecho fornecido/licenciado</label>
              <textarea id="customSnippet" className="zero-textarea mt-2" value={customSnippet} onChange={(event) => setCustomSnippet(event.target.value)} placeholder="Cole aqui só se você tiver permissão/licença" data-testid="zero-custom-snippet-textarea" />
            </div>
          </div>
          <div className="mt-4 grid gap-3 md:grid-cols-[1fr_auto]" data-testid="zero-custom-translation-block">
            <input className="zero-input" value={customTranslation} onChange={(event) => setCustomTranslation(event.target.value)} placeholder="Tradução em português do seu trecho" data-testid="zero-custom-translation-input" />
            <button type="button" className="primary-action w-auto" onClick={() => speak(customSnippet || clipPhrase, 0.76)} data-testid="zero-custom-practice-button">
              <Sparkles size={18} /> Praticar
            </button>
          </div>
        </article>
      </section>

      <section className="mt-8 neo-card surface-mint p-5" data-testid="zero-simple-ai-section">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="section-kicker" data-testid="zero-simple-ai-kicker">primeira conversa</p>
            <h2 className="font-display text-3xl font-black" data-testid="zero-simple-ai-title">Quando estiver pronto, vá para uma conversa bem simples.</h2>
            <p className="mt-2 text-sm font-bold text-slate-600" data-testid="zero-simple-ai-description">Comece com frases como “Hello”, “I need help” e “Can you repeat?”.</p>
          </div>
          <a href="/lesson/neighbor-intro" className="primary-action w-auto" data-testid="zero-start-simple-conversation-link">
            Começar conversa simples <RefreshCw size={18} />
          </a>
        </div>
      </section>
    </div>
  );
}